import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Components
import CardMemberAvatar from '@atoms/CardMemberAvatar';
import CheckboxWithoutForm from '@atoms/CheckboxWithoutForm';
import { PropertyChip } from '@molecules/PropertyChip';
import { BodyMedium, H4, LabelLarge } from '@styled';
import PropertyListItemListedIcons from './PropertyListItemListedIcons';
import { useDeleteListingModal } from '../../../../icsc-app/js/components/modals/DeletePropertyModal';

// Functionality
import { mediaQueries } from '@general/breakpoints';
import { useScreenMinMd, useScreenSmDown } from '@hooks/useResponsive';
import usePropertyDelete from '../../../../icsc-app/js/hooks/properties/usePropertyDelete';

const CheckboxAndImageContainerStyled = styled.div`
    display: flex;
    justify-content: center;

    @media ${mediaQueries.lg} {
        justify-content: flex-start;
    }
`;

const CheckboxContainerStyled = styled.div`
    align-items: center;
    display: flex;
    height: 64px;
    margin-right: 20px;
    padding-bottom: 20px;
    width: 20px;
`;

const ImageContainerStyled = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
    margin-top: 0;

    @media ${mediaQueries.md} {
        display: block;
        margin-bottom: 0;
    }
`;

const IconContainerSmall = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 16px;

    @media ${mediaQueries.md} {
        margin-top: 0;
    }
`;

const IconContainerMediumUp = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    margin-left: 1rem;

    @media ${mediaQueries.md} {
        position: absolute;
        right: 0;
        bottom: 0;
        top: 0;
    }
`;

const PropertyListItemListed = ({
    id,
    activatedAt,
    activations,
    coverPhotoUrl,
    displayLocation,
    expiresAt,
    internalUrl,
    isSelected = false,
    manager,
    populationDensity,
    purpose,
    recordId,
    size,
    squareFeet,
    title,
    typeOfCenter,
    openInNewTab = false,
    onClick = () => {},
    onDelete,
    onToggleSelected = () => {},
    hit,
}) => {
    const { openModal: openDeleteListingModal } = useDeleteListingModal();
    const removeProperty = usePropertyDelete({
        onCompleted: () => {
            onDelete();
        },
    });

    const isScreenSmDown = useScreenSmDown();
    const isScreenMinMd = useScreenMinMd();

    return (
        <div className="list-card py-4 my-0 w-100">
            <div className="d-block d-md-flex">
                <CheckboxAndImageContainerStyled>
                    <CheckboxContainerStyled>
                        <CheckboxWithoutForm checked={isSelected} onCheckChange={() => onToggleSelected(hit)} />
                    </CheckboxContainerStyled>

                    <ImageContainerStyled className="card-img-right">
                        {coverPhotoUrl && <img src={coverPhotoUrl} alt={title} className="image-cover" />}
                        {!coverPhotoUrl && (
                            <img src="/assets/images/company-default.svg" className="image-cover" alt={title} />
                        )}
                    </ImageContainerStyled>
                </CheckboxAndImageContainerStyled>

                <div className="d-block d-md-flex pl-0 w-100">
                    <div className="card-block m-0 ml-2 p-0 pl-3">
                        <Link
                            to={internalUrl}
                            className="card-link"
                            onClick={e => {
                                if (openInNewTab) {
                                    e.preventDefault();
                                    window.open(internalUrl, '_blank');
                                }

                                onClick({ hit });
                            }}
                        >
                            <H4 className="card-title display6 bold d-inline-block mb-0 align-middle mr-2">{title}</H4>

                            <div className="d-inline-block align-middle">
                                <PropertyChip
                                    activatedAt={activatedAt}
                                    expiresAt={expiresAt}
                                    activations={activations}
                                />
                            </div>

                            {displayLocation && (
                                <div className="mt-0.5rem mb-0.5rem">
                                    <LabelLarge>{displayLocation}</LabelLarge>
                                </div>
                            )}

                            <div className="text-dark-gray">
                                <BodyMedium>{purpose}</BodyMedium>
                                <BodyMedium>{populationDensity}</BodyMedium>
                                <BodyMedium>{typeOfCenter}</BodyMedium>
                                {squareFeet && <BodyMedium>{squareFeet} sq ft</BodyMedium>}
                                {!squareFeet && <BodyMedium>{size}</BodyMedium>}
                            </div>

                            <div className="d-flex align-items-center mt-1rem">
                                {manager && (
                                    <>
                                        <CardMemberAvatar
                                            photoURL={manager.photoUrl}
                                            firstName={manager.firstName}
                                            lastName={manager.lastName}
                                            fullName={`${manager.firstName} ${manager.lastName}`}
                                            sizeMobile={26}
                                            sizeDesktop={26}
                                        />

                                        <div className="ml-2">
                                            <BodyMedium>{`${manager.firstName} ${manager.lastName}`}</BodyMedium>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Link>
                    </div>

                    {isScreenSmDown && (
                        <IconContainerSmall>
                            <PropertyListItemListedIcons
                                id={id}
                                onDeleteClick={() => {
                                    openDeleteListingModal(title, () => removeProperty(id));
                                }}
                            />
                        </IconContainerSmall>
                    )}
                </div>

                {isScreenMinMd && (
                    <IconContainerMediumUp>
                        <PropertyListItemListedIcons
                            id={id}
                            onDeleteClick={() => {
                                openDeleteListingModal(title, () => removeProperty(id));
                            }}
                        />
                    </IconContainerMediumUp>
                )}
            </div>
        </div>
    );
};

PropertyListItemListed.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    activatedAt: PropTypes.string,
    activations: PropTypes.number.isRequired,
    coverPhotoUrl: PropTypes.string,
    displayLocation: PropTypes.string,
    expiresAt: PropTypes.string,
    internalUrl: PropTypes.string,
    isSelected: PropTypes.bool.isRequired,
    manager: PropTypes.object,
    populationDensity: PropTypes.string,
    purpose: PropTypes.string,
    recordId: PropTypes.number.isRequired,
    size: PropTypes.string,
    squareFeet: PropTypes.number,
    title: PropTypes.string.isRequired,
    typeOfCenter: PropTypes.string,
    onClick: PropTypes.func,
    onDelete: PropTypes.func,
    onToggleSelected: PropTypes.func,
    openInNewTab: PropTypes.bool,
    hit: PropTypes.object,
};

export default PropertyListItemListed;
